import { Page, PageHeader } from '@/shared/ui/page';
import {
  Spinner,
  TabsComponent,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { Home } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  useProposalAnalysisDetail,
  useProposalUnderwritingMessages,
} from '../hooks';
import { Step1Resume } from './proposal-analysis-steps/step-1-resume.component';
import { Step2Observations } from './proposal-analysis-steps/step-2-observations.component';
import { Step3Interactions } from './proposal-analysis-steps/step-3-interactions.component';

export type ProposalAnalysisTab = 'resume' | 'observations' | 'interactions';

interface ProposalAnalysisDetailsProps {
  proposalId: string;
  initialTab?: ProposalAnalysisTab;
}

export function ProposalAnalysisDetails({
  proposalId,
  initialTab = 'resume',
}: ProposalAnalysisDetailsProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ProposalAnalysisTab>(initialTab);

  const {
    data: detail,
    isLoading,
    error,
  } = useProposalAnalysisDetail(proposalId);

  const { data: underwritingData } =
    useProposalUnderwritingMessages(proposalId);

  const underwritingMessages = useMemo(() => {
    return (underwritingData?.messages ?? [])
      .map(message =>
        (message.messageDetail?.trim() || message.ruleName?.trim() || '').trim()
      )
      .filter(Boolean);
  }, [underwritingData?.messages]);

  const breadcrumb = (
    <Page.Breadcrumb
      items={[
        {
          label: 'Pagina Inicial',
          href: '/dashboard',
          iconLeft: <Home className='h-4 w-4' />,
        },
        { label: 'Subscrição' },
        { label: 'Propostas em Análise', href: '/proposal-analyses' },
        { label: 'Análise', active: true },
      ]}
    />
  );

  if (isLoading) {
    return (
      <Page.Root>
        {breadcrumb}
        <Page.Content>
          <div className='flex w-full items-center justify-center p-8'>
            <Spinner size='lg' colorScheme='blue' />
          </div>
        </Page.Content>
      </Page.Root>
    );
  }

  if (error || !detail) {
    return (
      <Page.Root>
        {breadcrumb}
        <Page.Content>
          <div className='flex flex-col gap-2 rounded-2xl bg-alba-white-500 p-8 shadow-[0px_4px_24px_0px_rgba(15,22,30,0.08)]'>
            <p className='font-montserrat text-base text-alba-black-500'>
              Proposta não encontrada.
            </p>
          </div>
        </Page.Content>
      </Page.Root>
    );
  }

  const daysRemainingColor: 'lightGreen' | 'warning' =
    detail.daysRemaining >= 20 ? 'lightGreen' : 'warning';

  return (
    <Page.Root>
      {breadcrumb}

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>
              Análise Proposta Empresarial - N° {detail.proposalNumber}
            </PageHeader.Title>
            <PageHeader.Subtitle>
              Analise os dados e registros da inspeção para validação da
              proposta
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>

          <PageHeader.Actions>
            <Tag.Root colorScheme={daysRemainingColor}>
              <Tag.Label>{detail.daysRemaining} Dias Restantes</Tag.Label>
            </Tag.Root>
          </PageHeader.Actions>
        </PageHeader.Main>
      </PageHeader.Root>

      <Page.Content className='py-4'>
        <TabsComponent.Root
          value={activeTab}
          onValueChange={details =>
            setActiveTab(details.value as ProposalAnalysisTab)
          }
        >
          <TabsComponent.List className='grid grid-cols-3'>
            <TabsComponent.Trigger value='resume'>
              Resumo da Proposta
            </TabsComponent.Trigger>
            <TabsComponent.Trigger value='observations'>
              Observações
            </TabsComponent.Trigger>
            <TabsComponent.Trigger value='interactions'>
              Registro de Interações
            </TabsComponent.Trigger>
          </TabsComponent.List>

          <TabsComponent.Content value='resume'>
            <Step1Resume
              detail={detail}
              underwritingMessages={underwritingMessages}
              onGoToDecision={() => setActiveTab('observations')}
              onExit={() => void navigate({ to: '/proposal-analyses' })}
            />
          </TabsComponent.Content>

          <TabsComponent.Content value='observations'>
            <Step2Observations
              proposalId={proposalId}
              underwritingMessages={underwritingMessages}
              requiresInspection={detail.proposal.requiresInspection}
              onBack={() => setActiveTab('resume')}
            />
          </TabsComponent.Content>

          <TabsComponent.Content value='interactions'>
            <Step3Interactions
              proposalId={proposalId}
              underwritingMessages={underwritingMessages}
            />
          </TabsComponent.Content>
        </TabsComponent.Root>
      </Page.Content>
    </Page.Root>
  );
}
